import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import { Calendar, Clock, ArrowLeft, ArrowRight, Loader2, Link as LinkIcon, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

// Set/replace a meta tag in <head> for OG / Twitter card
const upsertMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/api/blog/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
          setError('not_found');
          return;
        }
        if (!res.ok) throw new Error(`Failed to load post (${res.status})`);
        const data = await res.json();
        setPost(data);

        // SEO + OG tags
        document.title = `${data.title} — ResiAX | Homony`;
        const desc = data.excerpt || '';
        const url = `${window.location.origin}/blog/${data.slug}`;
        upsertMeta('name', 'description', desc);
        upsertMeta('property', 'og:title', data.title);
        upsertMeta('property', 'og:description', desc);
        upsertMeta('property', 'og:type', 'article');
        upsertMeta('property', 'og:url', url);
        const ogImg = data.cover_url || (data.images && data.images[0]) || null;
        if (ogImg) upsertMeta('property', 'og:image', ogImg);
        upsertMeta('name', 'twitter:card', ogImg ? 'summary_large_image' : 'summary');
        upsertMeta('name', 'twitter:title', data.title);
        upsertMeta('name', 'twitter:description', desc);
        if (ogImg) upsertMeta('name', 'twitter:image', ogImg);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main className="pt-24 pb-20">
        {loading && (
          <div className="flex items-center justify-center py-32" data-testid="blog-post-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#B5541B]" />
          </div>
        )}

        {error === 'not_found' && (
          <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="blog-post-404">
            <h1 className="text-3xl font-bold text-[#1F1E1E] mb-4">Post not found</h1>
            <p className="text-[#3B3535]/70 mb-8">We couldn't find that article. It might have been moved or unpublished.</p>
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-6 py-3 rounded-full transition-all"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </button>
          </div>
        )}

        {error && error !== 'not_found' && (
          <div className="max-w-2xl mx-auto px-4 py-32 text-center text-red-600" data-testid="blog-post-error">
            {error}
          </div>
        )}

        {post && !loading && !error && (
          <article data-testid="blog-post-article">
            {/* Cover */}
            {post.cover_url && (
              <div className="w-full h-64 sm:h-80 lg:h-[28rem] bg-[#FFF8F5] overflow-hidden flex items-center justify-center">
                <img src={post.cover_url} alt={post.title} className="w-full h-full object-contain" />
              </div>
            )}

            {/* Title block */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm text-[#3B3535]/60 hover:text-[#B5541B] mb-6 transition-colors"
                data-testid="blog-post-back"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Blog
              </Link>

              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {post.tags.map((t) => (
                    <span key={t} className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#B5541B]/10 text-[#B5541B]">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1E1E] leading-tight mb-6">
                {post.title}
              </h1>

              {post.excerpt && (
                <p className="text-lg text-[#3B3535]/80 leading-relaxed mb-8">
                  {post.excerpt}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-[#3B3535]/60 pb-8 mb-10 border-b border-gray-200">
                {post.author && (
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4" /> {post.author}
                  </span>
                )}
                {post.published_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" /> {formatDate(post.published_date)}
                  </span>
                )}
                {post.reading_time_minutes && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4" /> {post.reading_time_minutes} min read
                  </span>
                )}
              </div>

              {/* Body */}
              <div
                className="blog-content prose prose-lg max-w-none"
                data-testid="blog-post-body"
                dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
              />

              {/* References */}
              {post.references?.length > 0 && (
                <section className="mt-16 pt-8 border-t border-gray-200" data-testid="blog-post-references">
                  <h3 className="text-sm font-semibold text-[#B5541B] uppercase tracking-wide mb-4 flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" /> References
                  </h3>
                  <ul className="space-y-2">
                    {post.references.map((url, i) => (
                      <li key={i}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#B5541B] hover:text-[#8B3F14] underline break-all"
                        >
                          {url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* CTA */}
              <section className="mt-16 bg-gradient-to-r from-[#B5541B] to-[#8B3F14] rounded-2xl p-8 lg:p-10 text-white">
                <h3 className="text-xl lg:text-2xl font-bold mb-3">Want to shape the future of tenancy?</h3>
                <p className="text-white/90 mb-6 leading-relaxed">
                  Share what matters most to you and help us build a tool that genuinely puts residents first.
                </p>
                <Link
                  to="/resident-pilot#register"
                  className="inline-flex items-center gap-2 bg-white text-[#B5541B] font-semibold px-6 py-3 rounded-full hover:bg-[#FFF8F5] transition-all"
                  data-testid="blog-post-cta"
                >
                  Share What Matters <ArrowRight className="w-4 h-4" />
                </Link>
              </section>
            </div>
          </article>
        )}
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default BlogPostPage;
